import { EnvironmentClassSelect } from "@/components/EnvironmentClassSelect";
import { Button } from "@/components/flexkit/Button";
import { CheckboxInputField } from "@/components/flexkit/checkbox/CheckboxInputField";
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatRepoUrlForDisplay } from "@/format/url";
import { type EnvironmentClassEntry, useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { TrackLocations } from "@/hooks/use-segment";
import { getRepoUrlFromInitializer } from "@/queries/environment-queries";
import type { PlainProject } from "@/queries/project-queries";
import { useListProjectEnvironmentClasses } from "@/queries/project-queries";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";

type Props = {
    project: PlainProject;
    filteredEnvironmentClassIds?: string[];
    title?: string;
    description?: string;
    onClose: () => void;
    onSelect: (environmentClass: EnvironmentClassEntry, rememberChoice: boolean) => void;
};

export const SelectEnvironmentClassForProjectModal: FC<Props> = ({
    project,
    filteredEnvironmentClassIds,
    title,
    description,
    onClose,
    onSelect,
}) => {
    const { toast } = useToast();
    const [isChecked, setIsChecked] = useState<boolean>(true);

    const { environmentClassEntries, isLoading: isLoadingAllClasses } = useEnvironmentClassEntries();
    const { data: projectEnvironmentClasses, isLoading: isLoadingProjectClasses } = useListProjectEnvironmentClasses(
        project.id,
    );
    const [selectedEnvironmentClass, setSelectedEnvironmentClass] = useState<EnvironmentClassEntry | undefined>();

    const projectContextUrl = getRepoUrlFromInitializer(project.initializer);

    // Map project environment classes to their corresponding environment class entries
    const availableEnvironmentClasses = useMemo(() => {
        if (!projectEnvironmentClasses?.environmentClasses || !environmentClassEntries || isLoadingAllClasses) {
            return [];
        }

        let result: EnvironmentClassEntry[] = [];
        if (filteredEnvironmentClassIds) {
            result = filteredEnvironmentClassIds
                .map((id) => {
                    const entry = environmentClassEntries.find((entry) => entry.clazz.id === id);
                    return entry;
                })
                .filter((entry) => !!entry);
        } else {
            // Sort project environment classes by their order field, then map to entries
            const sortedProjectClasses = [...projectEnvironmentClasses.environmentClasses].sort(
                (a, b) => a.order - b.order,
            );

            for (const pec of sortedProjectClasses) {
                if (pec.environmentClass.case === "localRunner") {
                    // For local runners, include ALL local environment classes
                    const localEntries = environmentClassEntries.filter((entry) => entry.localRunner);
                    result.push(...localEntries);
                } else {
                    // For remote runners, find the specific environment class
                    const entry = environmentClassEntries.find(
                        (entry) => entry.clazz.id === pec.environmentClass.value,
                    );
                    if (entry) {
                        result.push(entry);
                    }
                }
            }
        }

        return result;
    }, [projectEnvironmentClasses, environmentClassEntries, isLoadingAllClasses, filteredEnvironmentClassIds]);

    // select the first environment class if none is selected
    useEffect(() => {
        if (!isLoadingAllClasses && !selectedEnvironmentClass && availableEnvironmentClasses.length > 0) {
            const firstActive = availableEnvironmentClasses[0];
            if (firstActive) {
                setSelectedEnvironmentClass(firstActive);
            }
        }
    }, [isLoadingAllClasses, selectedEnvironmentClass, availableEnvironmentClasses]);

    const isLoading = isLoadingAllClasses || isLoadingProjectClasses;

    const handleCreate = useCallback(() => {
        if (!selectedEnvironmentClass) {
            toast({
                title: "Please select an environment class",
                description: "You must select an environment class to create an environment.",
            });
            return;
        }
        onSelect(selectedEnvironmentClass, isChecked);
        onClose();
    }, [selectedEnvironmentClass, onSelect, onClose, toast, isChecked]);

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-[600px]" data-track-location={TrackLocations.CreateProjectModal}>
                <DialogHeader>
                    <DialogTitle>{title || "Create Environment"}</DialogTitle>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4 px-0 py-0">
                    {description && <p className="text-base leading-5 text-content-primary">{description}</p>}
                    <div
                        className="flex h-auto w-full flex-col rounded-xl border border-solid border-border-subtle bg-surface-primary px-4 py-3"
                    >
                        <div
                            className="truncate text-lg font-medium leading-6 text-content-primary"
                            title={project.metadata?.name}
                        >
                            {project.metadata?.name}
                        </div>
                        <div className="truncate text-base leading-5 text-content-secondary">
                            {formatRepoUrlForDisplay(projectContextUrl)}
                        </div>
                    </div>

                    <EnvironmentClassSelect
                        environmentClassEntries={availableEnvironmentClasses}
                        label="Environment Class"
                        value={selectedEnvironmentClass}
                        loading={isLoading}
                        disabled={false}
                        name="environment-class"
                        onChange={setSelectedEnvironmentClass}
                        sortEntries={false}
                    />
                </DialogBody>

                <DialogFooter className="justify-between items-center">
                    <CheckboxInputField
                        id="remember-choice"
                        checked={isChecked}
                        label="Remember my choice"
                        onChange={setIsChecked}
                    />
                    <Button
                        type="button"
                        variant="primary"
                        onClick={handleCreate}
                        disabled={!selectedEnvironmentClass}
                        data-tracking-id="create-environment-select-environment-class-for-project-modal"
                    >
                        Create Environment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
